/**
 * Voice agents service — the dealership's own voice-agent accounts.
 *
 * Mirrors ai.spectraiq.backend.voice.controller.VoiceAgentController
 * (/v1/voice-agents) and the provider catalogue at
 * /v1/voice-providers/catalog.
 *
 * Distinct from `voiceProviders.js`, which configures the STT/TTS pipeline —
 * genuinely one per tenant. This is the list of vendor agents the tenant runs:
 * a dealership may have Retell and Vapi live at the same time, each with its
 * own credential.
 *
 * Two things the backend will never give back:
 *
 *   apiKey    stored AES-256-GCM encrypted; responses carry `hasApiKey`
 *             instead. Sending null on update keeps the stored value.
 *   toolKey   returned exactly once, by create and rotate. Only its SHA-256 is
 *             kept, so a lost key cannot be recovered — it has to be rotated,
 *             which invalidates the one the vendor is currently using.
 *
 * `isActive` is a kill switch, not a selector. Each agent answers its own SIP
 * trunk and numbers, so the vendor picks up the call before this backend is
 * involved — there is nothing here to route. Toggling an agent off stops
 * accepting its webhooks and MCP tool calls.
 */
import { api } from '../api';

const unwrap = (r) => r.data?.data ?? r.data;

export default {
  /**
   * Vendors we can offer, for the provider dropdown.
   *
   * `isEnabled` is the intersection of the catalogue row and whether a client
   * implementation actually exists — a vendor with no client is listed but not
   * offerable, so the UI shows it disabled rather than letting someone
   * configure an agent that could never take a call.
   *
   * Also carries `agentIdLabel` ("Agent" vs "Assistant") and
   * `agentIdExample`, so the form uses each vendor's own wording.
   */
  catalog: () => api.get('/v1/voice-providers/catalog').then(unwrap),

  /** This tenant's agents. Never includes a plaintext key. */
  list: () => api.get('/v1/voice-agents').then(unwrap),

  get: (id) => api.get(`/v1/voice-agents/${id}`).then(unwrap),

  /**
   * Register an agent. Payload:
   *   { providerKey, agentId, agentName, apiKey, apiBase? }
   *
   * Verifies the credential with the vendor before activating, so a wrong key
   * comes back as status ERROR with `lastError` set rather than silently
   * failing on the first call. The response carries `toolKey` — show it once
   * and tell the operator to paste it into the vendor's MCP header config.
   *
   * `apiBase` is optional: null means the provider default. Set it only for a
   * regional endpoint or a mock.
   */
  create: (payload) => api.post('/v1/voice-agents', payload).then(unwrap),

  /**
   * Rename, or replace the credential. Sending `apiKey: null` keeps the stored
   * one; a new value replaces it and triggers re-verification. The tool key is
   * untouched — rotating that is deliberate and separate, because it breaks the
   * vendor's current config until the new one is pasted in.
   */
  update: (id, payload) => api.put(`/v1/voice-agents/${id}`, payload).then(unwrap),

  /** Remove the agent and clear its master-directory row. */
  remove: (id) => api.delete(`/v1/voice-agents/${id}`).then(unwrap),

  /** Flip the kill switch. Returns the updated agent. */
  toggle: (id) => api.post(`/v1/voice-agents/${id}/toggle`).then(unwrap),

  /**
   * Mint a replacement tool key. Returns { agentId, providerKey, toolKey }.
   * The previous key stops working the moment this commits, so the vendor's
   * MCP config must be updated or its next tool call gets a 401.
   */
  rotateToolKey: (id) => api.post(`/v1/voice-agents/${id}/rotate-tool-key`).then(unwrap),

  /**
   * The prompt this agent is currently running, read live from the vendor.
   *
   * Returns { engineType, prompt, beginMessage, model, toolNames, mcpUrls,
   * hasStates, note }. Read-only — nothing here writes a prompt back.
   *
   * `prompt` is null when it genuinely cannot be read, and `note` says why:
   * a custom-LLM agent keeps its prompt on the dealer's own server, and a
   * vendor may not expose prompts at all. Neither is an error.
   *
   * Nothing is cached server-side, so this reflects the vendor dashboard as it
   * stands right now — including edits made there since the agent was added.
   * A vendor that is unreachable comes back as a 502.
   */
  getPrompt: (id) => api.get(`/v1/voice-agents/${id}/prompt`).then(unwrap),

  /**
   * Replace the prompt the agent is running, at the vendor.
   *
   * Payload { prompt, expectedCurrent, force }. `expectedCurrent` is the text
   * the editor loaded: the backend compares it against what the vendor holds
   * right now and refuses with **409** if it moved, so a dealer's dashboard edit
   * is never wiped without someone seeing it. The 409 body carries `current` —
   * show it, then re-submit with `force: true` if they still want to overwrite.
   *
   * On success the response carries `effective`. False means the vendor accepted
   * the write but live calls will not use it yet (a pinned response-engine
   * version, typically) — `warning` says which. Do not show an unqualified
   * success in that case.
   */
  updatePrompt: (id, payload) => api.put(`/v1/voice-agents/${id}/prompt`, payload).then(unwrap),

  /**
   * SpectraIQ's recommended voice prompt, for the editor's Reset action.
   * Returns { prompt }. This only fills the editor — nothing is saved until the
   * operator presses Save.
   */
  recommendedPrompt: () => api.get('/v1/voice-agents/recommended-prompt').then(unwrap),

  /**
   * Re-check the stored credential against the vendor.
   * Returns { verified, agentName, detail } — a wrong key is an answer, not an
   * error, so this resolves with verified:false rather than rejecting.
   */
  test: (id) => api.post(`/v1/voice-agents/${id}/test`).then(unwrap),

  /**
   * Voice settings the agent is running, read live from the vendor (Retell).
   * Returns { voiceId, voiceSpeed, voiceTemperature, volume, language }.
   */
  getVoice: (id) => api.get(`/v1/voice-agents/${id}/voice`).then(unwrap),

  /** Voices the vendor account can use: [{ voiceId, name, provider, gender, accent, age, previewUrl }]. */
  listVoices: (id) => api.get(`/v1/voice-agents/${id}/voices`).then(unwrap),

  /** Change the voice. Only the fields sent change: { voiceId?, voiceSpeed?, voiceTemperature?, volume? }. */
  updateVoice: (id, payload) => api.put(`/v1/voice-agents/${id}/voice`, payload).then(unwrap),
};
